import { capturaApi, mensagemErroCaptura } from '@/features/captura/api/capturaApi';

/**
 * Vínculo "fotos da sessão → O.S." que falhou ao salvar a nova O.S. (rede
 * caiu bem na hora, por exemplo). Fica guardado neste aparelho e é
 * tentado de novo quando a O.S. for aberta com internet — a galeria chama
 * `tentarVinculosPendentes` antes de listar. Sem isso, as fotos ficariam
 * sem O.S. e o servidor apagaria em 7 dias.
 */

const CHAVE = 'captura:vinculos-pendentes';
/** O servidor apaga foto sem O.S. 7 dias depois da sessão expirar. */
const VALIDADE_MS = 7 * 86_400_000;

type VinculoPendente = { sessaoId: string; orcamentoId: string; em: number };

function ler(): VinculoPendente[] {
  try {
    const bruto = localStorage.getItem(CHAVE);
    if (!bruto) return [];
    const lista = JSON.parse(bruto) as unknown;
    if (!Array.isArray(lista)) return [];
    const agora = Date.now();
    return lista.filter(
      (v): v is VinculoPendente =>
        !!v &&
        typeof v.sessaoId === 'string' &&
        typeof v.orcamentoId === 'string' &&
        typeof v.em === 'number' &&
        agora - v.em < VALIDADE_MS,
    );
  } catch {
    return [];
  }
}

function gravar(lista: VinculoPendente[]): void {
  try {
    if (lista.length === 0) localStorage.removeItem(CHAVE);
    else localStorage.setItem(CHAVE, JSON.stringify(lista));
  } catch {
    // armazenamento bloqueado: segue sem a fila
  }
}

export function guardarVinculoPendente(sessaoId: string, orcamentoId: string): void {
  const lista = ler().filter((v) => v.sessaoId !== sessaoId);
  lista.push({ sessaoId, orcamentoId, em: Date.now() });
  gravar(lista);
}

/** Tenta os vínculos pendentes desta O.S. Devolve quantas fotos foram vinculadas. */
export async function tentarVinculosPendentes(orcamentoId: string): Promise<number> {
  const lista = ler();
  const daOs = lista.filter((v) => v.orcamentoId === orcamentoId);
  if (daOs.length === 0) {
    gravar(lista); // aproveita para descartar os vencidos
    return 0;
  }
  let vinculadas = 0;
  const resolvidos = new Set<string>();
  for (const v of daOs) {
    try {
      const r = await capturaApi.vincularSessao(v.sessaoId, v.orcamentoId);
      vinculadas += r?.vinculadas ?? 0;
      resolvidos.add(v.sessaoId);
    } catch {
      // continua pendente
    }
  }
  gravar(ler().filter((v) => !resolvidos.has(v.sessaoId)));
  return vinculadas;
}

export type ResultadoVinculo = {
  vinculadas: number;
  /** Mensagem amigável quando alguma sessão não pôde ser vinculada agora. */
  erro: string | null;
};

/**
 * Vincula as fotos das sessões à O.S. recém-salva. Falha não derruba nada:
 * a sessão vai para a fila local e a mensagem volta para a tela avisar.
 */
export async function vincularSessoesAoOrcamento(
  sessoes: string[],
  orcamentoId: string,
): Promise<ResultadoVinculo> {
  let vinculadas = 0;
  let erro: string | null = null;
  for (const id of sessoes) {
    try {
      const r = await capturaApi.vincularSessao(id, orcamentoId);
      vinculadas += r?.vinculadas ?? 0;
    } catch (e) {
      guardarVinculoPendente(id, orcamentoId);
      erro ??= mensagemErroCaptura(e, 'Não foi possível vincular as fotos agora.');
    }
  }
  return { vinculadas, erro };
}
